/**
 * Supabase client configuration
 *
 * For an experience like Myst/Riven, we might use Supabase for:
 * - User progress/state persistence
 * - Unlocked scenes/discoveries
 * - Ambient audio preferences
 * - Community secrets/shared discoveries
 *
 * NOTE: Current runtime does not consume this client yet.
 * When adding Supabase usage, always guard against null:
 *   if (supabase) { ... }
 * Do NOT use `!` assertions or `as` casts to bypass null checks.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/types/database';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

/**
 * Supabase client instance.
 * Returns `null` when environment variables are not configured (local dev mode).
 */
export const supabase: SupabaseClient<Database> | null = ((): SupabaseClient<Database> | null => {
  if (!supabaseUrl || !supabaseAnonKey) {
    console.warn('⚠️ Supabase not configured – running in local-only mode');
    return null;
  }

  return createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
    },
  });
})();

/**
 * Save a subscriber email to the database.
 * Silently handles duplicates and failures (fire-and-forget).
 * @param email - The email address to save
 * @param source - Where the signup came from (default: 'landing_gate')
 */
export async function saveSubscriber(email: string, source = 'landing_gate'): Promise<boolean> {
  if (!supabase) {
    console.warn('⚠️ Supabase not configured – email not saved:', email);
    return false;
  }

  type SubscriberInsert = Database['public']['Tables']['subscribers']['Insert'];
  const payload: SubscriberInsert = { email, source };

  const { error } = await supabase
    .from('subscribers')
    // @ts-expect-error - Table exists at runtime, types may be stale
    .insert(payload);

  if (error && typeof error === 'object' && 'code' in error) {
    const pgError = error as { code: string; message: string };
    // Duplicate email (23505) is fine, just log others
    if (pgError.code !== '23505') {
      console.error('Failed to save subscriber:', pgError.message);
    }
    return false;
  }

  return true;
}

export type { Database } from '@/types/database';

/* ── Products ────────────────────────────────────────────────── */

export interface Product {
  id: string;
  name: string;
  incantation: string;
  image: string;
  href: string;
}

/**
 * Fetch visible products for a collection, ordered by sort_order.
 * Returns `null` when Supabase is unavailable or the query fails,
 * so callers can fall back to hardcoded items.
 */
export async function fetchProducts(collection: string): Promise<Product[] | null> {
  if (!supabase) return null;

  const { data, error } = await supabase
    .from('products')
    .select('id, name, incantation, image, href')
    .eq('collection', collection)
    .eq('visible', true)
    .order('sort_order');

  // Runtime network/query failures are possible despite static types
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (error || !data) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    console.warn(`⚠️ Failed to fetch products for "${collection}":`, error?.message);
    return null;
  }

  return data as Product[];
}
